import React from 'react';
import { Navigate, Route, Routes as RouterRoutes, useLocation, useParams } from 'react-router';
import { useSelector } from 'react-redux';

import NotFound from 'views/NotFound';
import Topbar from 'components/Navigation';
import BrTabs from 'views/BrTabs/BrTabs';
import CaseRedirect from 'views/CaseRedirect/CaseRedirect';
import Home from 'views/Home/Home';
import PartnerFrontendIFrame from 'views/Frames/PartnerFrontendIFrame';
import StructureFormTabs from 'views/PartnerTabs/StructureFormTabs';
import LegalPartnerFormTabs from 'views/PartnerTabs/LegalPartnerFormTabs';
import PrivateIndividualFormTabs from 'views/PartnerTabs/PrivateIndividualFormTabs';
import GlobalPartnerHandlingDeskIFrame from 'views/Frames/GlobalPartnerHandlingDeskIFrame';
import DocumentCenter from 'views/DocumentCenter/DocumentCenter';
import { useRuntimeConfig } from 'config/useRuntimeConfig';
import { JBErrorModalProps, JBErrorView, JBLoadingLayout } from '@bjb-gcrm/gcrm-frontend-components';
import { isNotNullOrUndefined, isStringNotBlank } from 'utils/utils';
import { User } from 'types/User';
import { GlobalState } from 'reducers/stateTypes';
import PageTitleToken from 'components/Common/PageTitleToken';
import { useHostRouteSync } from 'hooks/useHostRouteSync';
import WeNaTopbar from 'components/Navigation/WeNaTopbar';
import { useHost } from 'contexts/Host';

export const routeList = {
  brCoreData: (brId: string) => `/business-relation/edit/${brId}/core-data`,
  distributionCenter: (tab: string, id: string = '') => {
    if (id) {
      return `/distribution-center/${tab}/${id}`;
    }
    return `/distribution-center/${tab}`;
  },
  documentCenter: (tab: string, id: string = '') => {
    if (id) {
      return `/document-center/${tab}/${id}`;
    }
    return `/document-center/${tab}`;
  },
  home: (tab: string, subTab?: string, requestUId?: string) => {
    // Special case for adhoc-screening - requestUId goes in path, not query
    if (tab === 'adhoc-screening' && isStringNotBlank(requestUId)) {
      return `/home/adhoc-screening/${requestUId}`;
    }

    const basePath = subTab ? `/home/${tab}/${subTab}` : `/home/${tab}`;
    const queryString = isStringNotBlank(requestUId) ? `?requestUId=${requestUId}` : '';
    return `${basePath}${queryString}`;
  },
};

// Component to handle partner edit route with query params
function PartnerEditWrapper() {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const location = useLocation();

  // Get query parameters using URLSearchParams
  const queryParams = new URLSearchParams(location.search);

  // Get specific query parameter values
  const wizardBrUid = queryParams.get('wizardBrUid');
  const wizardRole = queryParams.get('wizardRole');

  return (
    <PartnerFrontendIFrame
      path={`/partner/edit/${id}/${tab || ''}?wizardBrUid=${wizardBrUid ?? ''}&wizardRole=${wizardRole ?? ''}`}
    />
  );
}

function RouteList() {
  const { search } = useLocation();

  return (
    <RouterRoutes>
      <Route path="/" element={<Navigate to={{ pathname: '/partners', search }} replace />} />
      <Route path="/partner-list" element={<Navigate to={{ pathname: '/partners', search }} replace />} />
      <Route path="/new-partner" element={<Navigate to="/partner/new/private-individual" replace />} />
      <Route path="/home" element={<Navigate to="/home/tasks/my-tasks" replace />} />
      <Route path="/home/tasks" element={<Navigate to="/home/tasks/my-tasks" replace />} />
      <Route path="/home/adhoc-screening/bulk/:batchId/status" element={<Home />} />
      <Route path="/home/adhoc-screening/:requestUId?" element={<Home />} />
      <Route path="/home/:tab" element={<Home />} />
      <Route path="/home/:tab/:subTab" element={<Home />} /> {/* Business relation routes */}
      <Route path="/business-relation/edit" element={<BrTabs />} />
      <Route path="/business-relation/edit/:brId" element={<BrTabs />} />
      <Route path="/business-relation/edit/:brId/:tab" element={<BrTabs />} />
      <Route path="/case/:caseId" element={<CaseRedirect />} />
      {/* Partner edit routes - using wrapper component */}
      <Route
        path="/partners"
        element={
          <>
            <PageTitleToken />
            <PartnerFrontendIFrame path={`/partners${search}`} />
          </>
        }
      />
      <Route path="/partner/edit/:id" element={<PartnerEditWrapper />} />
      <Route path="/partner/edit/:id/:tab" element={<PartnerEditWrapper />} />
      <Route path="/view/:id" element={<PartnerEditWrapper />} />
      <Route path="/view/:id/:tab" element={<PartnerEditWrapper />} />
      <Route path="/partner/private-individual/edit/:id" element={<PrivateIndividualFormTabs />} />
      <Route path="/partner/private-individual/edit/:id/:tab" element={<PrivateIndividualFormTabs />} />
      <Route path="/partner/structure/edit/:id" element={<StructureFormTabs />} />
      <Route path="/partner/structure/edit/:id/:tab" element={<StructureFormTabs />} />
      <Route path="/partner/legal-entity/edit/:id" element={<LegalPartnerFormTabs />} />
      <Route path="/partner/legal-entity/edit/:id/:tab" element={<LegalPartnerFormTabs />} />
      <Route path="/document-center" element={<DocumentCenter />} />
      <Route path="/document-center/:tab" element={<DocumentCenter />} />
      <Route path="/document-center/:tab/:id" element={<DocumentCenter />} />
      <Route path="/gphd" element={<GlobalPartnerHandlingDeskIFrame path="/gphd" />} />
      <Route path="/not-found" element={<NotFound />} />
      <Route path="*" element={<Navigate to="/not-found" replace />} /> {/* Catch-all route */}
    </RouterRoutes>
  );
}
const Routes = () => {
  const runtimeConfig = useRuntimeConfig();
  useHostRouteSync();
  const { isWena } = useHost();
  const user: User = useSelector((state: GlobalState) => state.user.userDetails);
  const queryParams = new URLSearchParams(location.search);
  const hideTopbar = queryParams.get('hideTopbar') === 'true';
  if (isNotNullOrUndefined(runtimeConfig.error)) {
    return (
      <>
        <Topbar />
        <div data-testid="runtime-config-error">
          <JBErrorView
            hideCloseButton={true}
            handleClose={() => {}}
            httpInfoAxiosError={runtimeConfig.error as JBErrorModalProps['httpInfoAxiosError']}
            technicalInfo={{
              details: runtimeConfig.error?.message ?? 'Unknown technical details',
              knownIssue: 'REFRESH_PAGE_THEN_OPEN_TICKET',
              envInfo: {
                envId: 'UNKNOWN',
                appId: 'clm-portal-frontend',
                componentId: 'clm-portal-frontend',
              },
              userInfo: {
                userId: user?.username ?? 'unknown user',
              },
              title: 'Failed to load runtime configuration',
              userErrorMessage: 'Failed to load runtime configuration',
            }}
          />
        </div>
      </>
    );
  }

  if (runtimeConfig.isLoading) {
    return (
      <>
        {isWena ? <WeNaTopbar /> : <Topbar />}
        <JBLoadingLayout />
      </>
    );
  }

  return (
    <>
      {hideTopbar || (isWena ? <WeNaTopbar /> : <Topbar />)}
      <RouteList />
    </>
  );
};

export default Routes;
